import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis } from 'apis/pilotAPI';
import { Environment } from 'types/apis/pilotInterfaces';
import { StudioModal } from './StudioModal';
import styles from './StudioModal.module.scss';
import { AWS_REGION_OPTIONS, normalizeAwsRegion } from './environmentFormConstants';

interface EditEnvironmentDialogProps {
  open: boolean;
  environment: Environment | null;
  onClose: () => void;
  onSuccess: () => void;
}

const awsRegionEnumValues = AWS_REGION_OPTIONS.map((o) => o.value) as [string, ...string[]];
const editEnvironmentSchema = z.object({
  environment_name: z.string().min(1, 'Environment name is required.').max(100),
  aws_region: z.enum(awsRegionEnumValues, { required_error: 'AWS Region is required.' }),
  data_server_url: z.string().url('Invalid URL format.').min(1, 'Data server URL is required.'),
  // allow empty string (optional) OR a valid URL
  frontend_url: z.union([z.string().url('Invalid URL format.'), z.literal('')]),
});

type EditEnvironmentFormValues = z.infer<typeof editEnvironmentSchema>;

export const EditEnvironmentDialog: React.FC<EditEnvironmentDialogProps> = ({
  open,
  environment,
  onClose,
  onSuccess,
}) => {
  const { rc } = useGlobalConfig();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultValues = useMemo<EditEnvironmentFormValues>(
    () => ({
      environment_name: '',
      aws_region: 'NA',
      data_server_url: '',
      frontend_url: '',
    }),
    []
  );

  const form = useForm<EditEnvironmentFormValues>({
    resolver: zodResolver(editEnvironmentSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (open && environment) {
      setError(null);
      const serverUrl =
        environment.serverUrl ??
        `https://api-server-${environment.stack_name}-uat.bidgely.com`;
      form.reset({
        environment_name: environment.environment_name ?? '',
        aws_region: normalizeAwsRegion(environment.aws_region),
        data_server_url: serverUrl,
        frontend_url: environment.frontend_url ?? '',
      });
    }
  }, [open, environment, form]);

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  const onSubmitForm = async (values: EditEnvironmentFormValues) => {
    if (!environment) return;
    setSubmitting(true);
    try {
      const response = await PilotApis.updateEnvironment(rc, environment.id, {
        environment_name: values.environment_name,
        data_server_url: values.data_server_url,
        frontend_url: values.frontend_url || '',
        aws_region: values.aws_region,
      });
      if (response.error) {
        setError(
          typeof response.error === 'string'
            ? response.error
            : (response.error as any)?.message || 'Failed to update environment'
        );
        return;
      }
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err?.message || 'An error occurred while updating the environment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!environment) return null;

  return (
    <StudioModal
      open={open}
      onClose={handleClose}
      title="Edit Environment"
      description="Modify the details for this environment. Click save when you're done."
      footer={
        <>
          <button
            type="button"
            className={`${styles.button} ${styles.outlineButton}`}
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-environment-form"
            className={`${styles.button} ${styles.primaryButton}`}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }
    >
      <form
        id="edit-environment-form"
        className={styles.form}
        onSubmit={form.handleSubmit(onSubmitForm)}
      >
        {error ? <div className={styles.errorBox}>{error}</div> : null}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-environment_name">
            Environment name
          </label>
          <input
            id="edit-environment_name"
            className={styles.input}
            placeholder="e.g., uat"
            disabled={submitting}
            {...form.register('environment_name')}
          />
          {form.formState.errors.environment_name?.message ? (
            <div className={styles.errorText}>{form.formState.errors.environment_name.message}</div>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-aws_region">
            AWS Region
          </label>
          <select
            id="edit-aws_region"
            className={styles.select}
            disabled={submitting}
            {...form.register('aws_region')}
          >
            {AWS_REGION_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {form.formState.errors.aws_region?.message ? (
            <div className={styles.errorText}>{form.formState.errors.aws_region.message}</div>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-data_server_url">
            Data server URL
          </label>
          <input
            id="edit-data_server_url"
            className={styles.input}
            placeholder="https://..."
            disabled={submitting}
            {...form.register('data_server_url')}
          />
          {form.formState.errors.data_server_url?.message ? (
            <div className={styles.errorText}>{form.formState.errors.data_server_url.message}</div>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-frontend_url">
            Frontend URL
          </label>
          <input
            id="edit-frontend_url"
            className={styles.input}
            placeholder="https://..."
            disabled={submitting}
            {...form.register('frontend_url')}
          />
          {form.formState.errors.frontend_url?.message ? (
            <div className={styles.errorText}>{form.formState.errors.frontend_url.message as string}</div>
          ) : null}
          <div className={styles.help}>Optional.</div>
        </div>
      </form>
    </StudioModal>
  );
};
